/**
 * Web-only design tokens for the ZenBasket POS browser build.
 *
 * This file is the single source of truth for every dimension the web UI
 * standardises: typography, control sizing, spacing, radii, modal/popover
 * sizing and stacking order. Nothing here is imported by Metro, so Android and
 * tablet builds are untouched.
 *
 * IMPORTANT - this is *not* a new visual language. The values below are taken
 * from the design system the project already has:
 *
 *   src/themes/appTheme.tsx        -> appColors, appFonts, useFontSize()
 *   zen-core-elements/src/shared/constants.ts -> appColors, fontSize
 *
 * The two scales disagree (appTheme keys off `isLandscape`, zen-core-elements
 * keys off Dimensions.width), which is why a screen built from app components
 * and a screen built from Zen components render body text at different sizes in
 * the same browser window. The typography block below adopts the *app* scale -
 * appTheme's landscape values - and `zenConstants.web.ts` feeds the same
 * numbers back into zen-core-elements so both halves finally agree.
 */

/* ------------------------------------------------------------------ *
 * Typography
 * ------------------------------------------------------------------ */

/** Families as registered by src/platform/fonts/webFonts.web.ts. */
export const webFontFamily = {
  regular: 'Montserrat-Medium',
  bold: 'Montserrat-SemiBold',
} as const;

/**
 * CSS fallback chain for any DOM node that is not styled by react-native-web
 * (the document body, native <input> chrome, print output, ...). Kept to fonts
 * that ship with the OS so no extra network dependency is introduced.
 */
export const webFontStack =
  "'Montserrat-Medium', 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif";

/**
 * The app scale (appTheme.tsx, landscape column) mapped onto the roles listed
 * in the UI spec. Desktop browsers are always "landscape", so these are the
 * numbers appTheme would have produced anyway.
 */
export const webFontSize = {
  pageTitle: 20, // headerFontSize - anchors the page, unchanged from native
  sectionTitle: 16, // largerFontSize
  cardTitle: 16,
  body: 15, // mediumFontSize
  input: 14, // smallerFontSize
  placeholder: 14,
  button: 16, // largerFontSize - matches ZenButton md
  label: 14, // smallerFontSize
  helper: 13, // smallFontSize
  table: 14,
  menu: 14,
  modalTitle: 16,
  modalContent: 15,
  popover: 15,
  error: 12, // errorFontSize
  badge: 10, // badgeFontSize
  icon: 18, // iconSize
} as const;

/**
 * Explicit line heights. react-native-web derives line boxes from the font
 * metrics when lineHeight is omitted, and those metrics differ from the ones
 * the native text engine uses - which is how identical styles end up with
 * different row heights on web. Pinning them keeps rows stable.
 */
export const webLineHeight = {
  tight: 1.2,
  normal: 1.4,
  /** Rounded absolute line height for a given font size. */
  of(fontSize: number, ratio: number = 1.4): number {
    return Math.round(fontSize * ratio);
  },
} as const;

/* ------------------------------------------------------------------ *
 * Spacing
 * ------------------------------------------------------------------ */

export const webSpacing = {
  xxs: 2,
  xs: 4,
  sm: 8,
  md: 12,
  lg: 16,
  xl: 20,
  xxl: 24,
} as const;

/* ------------------------------------------------------------------ *
 * Form controls
 * ------------------------------------------------------------------ */

export const webControl = {
  /** Single standard height shared by inputs, dropdowns and md buttons. */
  height: 40,
  borderRadius: 5,
  borderWidth: 1,
  paddingHorizontal: 10,
  /**
   * The height of a *bordered* control - the outer box, borders included.
   *
   * Most screens set `height: 40` on the inner input, which with a 1px
   * border on each side makes the field box 42. A field that does not set
   * its own input height falls back to the container minimum instead and
   * lands at 40, so two fields in the same column end up 2px apart -
   * measured on Product Unit Count against every other field on the Add
   * Product form. Deriving the box height from the control height means the
   * fallback matches the common case instead of undercutting it.
   */
  boxHeight: 40 + 2 * 1,
} as const;

/**
 * The field wrapper contract: label, control, and a validation slot that is
 * *always* the same height whether or not there is a message in it.
 *
 * Why this matters on web specifically: ZenInput already renders its error
 * <Text> unconditionally (`content={errorMessage || ''}`). On native an empty
 * Text still occupies one line box, so the space is reserved and neighbouring
 * fields never move. react-native-web renders that same empty Text as an empty
 * <div>, which collapses to 0px - so the field grows the moment a message
 * appears and every control below it jumps down. `errorMinHeight` restores the
 * reserved line.
 */
export const webField = {
  labelFontSize: webFontSize.label,
  labelMarginBottom: 5,
  labelLineHeight: webLineHeight.of(webFontSize.label, 1.3),

  errorFontSize: webFontSize.error,
  errorLineHeight: webLineHeight.of(webFontSize.error, 1.35),
  /** Reserved validation slot - one line of error text, always present. */
  errorMinHeight: webLineHeight.of(webFontSize.error, 1.35) + 2,
  errorMarginTop: 2,
  /**
   * A long message wraps instead of stretching the row sideways; two lines is
   * the ceiling before it is ellipsised, so a verbose server message can never
   * redistribute the whole form.
   */
  errorMaxLines: 2,

  /** Vertical rhythm between stacked fields, and between columns in a row. */
  rowGap: 12,
  columnGap: 16,
} as const;

/* ------------------------------------------------------------------ *
 * Buttons
 * ------------------------------------------------------------------ */

/**
 * ZenButton derives its height from `paddingVertical` on the *title text*,
 * so the rendered height depends on the font metrics of the label. On web
 * those metrics differ from native, which is where the uneven button rows come
 * from. The web wrapper pins a minHeight per size instead; `md` deliberately
 * equals webControl.height so a button sitting next to an input lines up.
 *
 * minWidth is the same idea for the other axis (16.1, 16.2). ZenButton has no
 * width of its own either, so a button is exactly as wide as whatever is
 * inside it: it changes size when the label does ("Save" becomes "Update"),
 * and again when the loading prop swaps the label out for a spinner. A floor
 * per size removes both. The md value is 100 because that is the width the app
 * already writes by hand in eighteen places, so the floor agrees with the
 * buttons that are sized explicitly rather than competing with them.
 */
export const webButton = {
  sm: {minHeight: 32, minWidth: 80, fontSize: webFontSize.helper, paddingHorizontal: 12},
  md: {minHeight: webControl.height, minWidth: 100, fontSize: webFontSize.button, paddingHorizontal: 16},
  lg: {minHeight: 48, minWidth: 120, fontSize: webFontSize.pageTitle, paddingHorizontal: 20},
  borderRadius: 7,
  /** Gap between a leading/trailing icon and the label. */
  iconGap: 6,
  iconSize: 18,
  /** Keeps [Cancel] [Save] pairs evenly spaced wherever they appear. */
  groupGap: 10,
} as const;

/* ------------------------------------------------------------------ *
 * Modals, popovers, overlays
 * ------------------------------------------------------------------ */

export const webModal = {
  /** Width ladder used by the shared modal card (see themes/webOverrides). */
  maxWidth: 960,
  /** Never taller than the viewport; content scrolls inside instead. */
  maxHeightRatio: 0.9,
  /** Breathing room so the card never touches the browser edge. */
  viewportInset: 32,
  borderRadius: 10,
  padding: webSpacing.xl,
  headerPaddingVertical: 12,
  footerPaddingVertical: 12,
  overlayColor: 'rgba(0, 0, 0, 0.5)',
} as const;

export const webPopover = {
  minWidth: 200,
  /** Hard ceiling; the effective value is also clamped to the viewport. */
  maxWidth: 720,
  maxHeightRatio: 0.85,
  viewportInset: 24,
  borderRadius: 10,
} as const;

/**
 * One stacking scale for the whole web app. Anything that floats reads its
 * z-index from here rather than inventing a local number, which is what keeps
 * a dropdown from rendering above the modal that owns it.
 */
export const webZIndex = {
  base: 0,
  sticky: 100,
  dropdown: 1000,
  popover: 1100,
  modal: 1200,
  toast: 1300,
  tooltip: 1400,
} as const;

/* ------------------------------------------------------------------ *
 * Colours used by the web-only chrome (focus ring, overlays, scrollbars)
 * ------------------------------------------------------------------ */

export const webColor = {
  primary: '#005374',
  focusRing: '#005374',
  focusRingSoft: 'rgba(0, 83, 116, 0.35)',
  border: '#dddddd',
  /** Field border under the pointer - a visible affordance, same weight. */
  borderHover: '#b3b3b3',
  /**
   * The surface an autofilled field shows. White rather than Chrome's pale
   * blue so an autofilled field reads as a filled field, not as a browser
   * artefact - and so it works on the dark sign-in panel as well as on the
   * white forms.
   */
  autofill: '#ffffff',
  text: '#414141',
  error: '#d72c0c',
  overlay: webModal.overlayColor,
  scrollbarThumb: 'rgba(0, 0, 0, 0.25)',
  scrollbarTrack: 'transparent',
} as const;

/** Viewport-aware width helper shared by the modal and popover wrappers. */
export function clampToViewport(
  preferred: number,
  viewport: number,
  inset: number,
): number {
  return Math.max(0, Math.min(preferred, viewport - inset * 2));
}

/* ------------------------------------------------------------------ *
 * Search fields
 * ------------------------------------------------------------------ */

/**
 * 14.11 - one search bar for the whole web app.
 *
 * The app has three separate search implementations (zen-core's ZenSearchBar,
 * the sell screen's CustomSearchBar, and a few bare TextInputs), each with its
 * own height, radius, icon size and font. They sit on the same toolbars, so
 * the difference is visible. These are the numbers the web wrappers use; they
 * deliberately reuse the control tokens above so a search field, a text input
 * and an `md` button in the same row share a height and a radius.
 */
export const webSearch = {
  height: webControl.height,
  borderRadius: webControl.borderRadius,
  borderWidth: webControl.borderWidth,
  paddingHorizontal: webControl.paddingHorizontal,
  /** Space between the leading icon, the text and the clear button. */
  gap: webSpacing.sm,
  iconSize: webFontSize.icon,
  fontSize: webFontSize.input,
} as const;

/* ------------------------------------------------------------------ *
 * Sell-screen dialogs
 * ------------------------------------------------------------------ */

/**
 * Widths for the dialogs built on the shared sell-screen card
 * (components/newSellscreen/sellscreenModal.tsx).
 *
 * Seven of those dialogs size their body as a fraction of the window -
 * `minWidth: width > 900 ? width * 0.5 : ...` - which is a sensible phone
 * heuristic and a poor desktop one: at a 1600px window it demands 800px, and
 * at 2560px it demands 1280px, neither of which has anything to do with how
 * much content there is. Worse, it collides with the card's own ceiling, and
 * a minWidth larger than the parent's maxWidth does not shrink - it spills out
 * of the card, which is exactly the Product Details dialog overflowing its
 * panel on the right.
 *
 * Two steps, rather than a per-dialog number: a short dialog and a list
 * dialog, both clamped to the viewport.
 */
export const webDialog = {
  /** A single narrow list - the location picker. */
  sm: 420,
  /** Prompts and short forms - tip, additional discount, a single payment. */
  md: 520,
  /** A list or a two-column body - product details, payment methods. */
  lg: 720,
  /**
   * Ceiling for the shared card itself: the widest body (lg) plus the card's
   * own 16px padding on each side, with a little slack.
   */
  maxWidth: 760,
  /**
   * Taken off the viewport before clamping. Covers the card's 20px margin and
   * 16px padding on each side, so the card can always shrink to fit a narrow
   * browser window instead of overflowing it.
   */
  viewportInset: 40,
  /** Never narrower than this, however small the window gets. */
  minWidth: 320,
} as const;
